import type { Store, Where } from './port';
import type { TableName } from './rows';
import { TABLES, SCHEMA_VERSION } from './schema';

/**
 * Ablage für die Entwicklung im Browser.
 *
 * Warum nicht `jeep-sqlite`: der übliche Web-Unterbau des SQLite-Plugins ist am 2024-08-16
 * stehengeblieben und fällt damit durch Regel 3 der Plugin-Regeln. Ein Objektspeicher je
 * Tabelle tut hier dasselbe, ohne Abhängigkeit.
 *
 * ⚠️ Dieser Adapter prüft **keine** Fremdschlüssel — das kann nur der native Weg. Er dient der
 * Entwicklung, nicht dem Prüfungsbetrieb.
 */

/**
 * Die Fassung der IndexedDB — **dieselbe Zahl wie `SCHEMA_VERSION`**.
 *
 * ⚠️ **Sie stand bis zum 2026-09-18 als eigene Ziffer hier** und wurde von niemandem
 * abgeglichen. Bei Fassung 5 fiel es auf: der Aufstieg war in `schema.ts`, in `sqlite.ts` und
 * in der Testkette nachgezogen, nur im Browser blieb die Ablage auf 4 stehen — `onupgradeneeded`
 * lief nie, die neue Spalte fehlte, und **kein Test hätte das gemeldet** (die Tests fahren die
 * SQL-Kette). Aufgefallen ist es erst, weil ich im Browser nachgesehen habe.
 *
 * Jetzt ist es keine zweite Wahrheit mehr, sondern die eine.
 */
export const SCHEMA_FASSUNG = SCHEMA_VERSION;

interface Gemerkt {
  table: TableName;
  vorher: unknown[];
}

export function createWebStore(name = 'osce'): Store {
  let db: IDBDatabase | null = null;
  /** Während einer Transaktion: der Stand vor der ersten Änderung je Tabelle. */
  let rückweg: Gemerkt[] | null = null;

  const warten = <T>(anfrage: IDBRequest<T>): Promise<T> =>
    new Promise((gelöst, verworfen) => {
      anfrage.onsuccess = () => gelöst(anfrage.result);
      anfrage.onerror = () => verworfen(anfrage.error);
    });

  const offen = (): IDBDatabase => {
    if (!db) throw new Error('Ablage nicht geöffnet — open() fehlt.');
    return db;
  };

  const passt = (satz: Record<string, unknown>, where?: Where) => {
    if (!where) return true;
    return Object.entries(where).every(([feld, wert]) => (satz[feld] ?? null) === wert);
  };

  const lesen = async <T>(table: TableName): Promise<T[]> => {
    const tx = offen().transaction(table, 'readonly');
    return warten(tx.objectStore(table).getAll() as IDBRequest<T[]>);
  };

  const merken = async (table: TableName) => {
    if (!rückweg || rückweg.some((g) => g.table === table)) return;
    rückweg.push({ table, vorher: await lesen(table) });
  };

  const wartenTx = (tx: IDBTransaction): Promise<void> =>
    new Promise((gelöst, verworfen) => {
      tx.oncomplete = () => gelöst();
      tx.onerror = () => verworfen(tx.error);
      tx.onabort = () => verworfen(new Error('Transaktion abgebrochen'));
    });

  const ablage: Store = {
    async open() {
      db = await new Promise<IDBDatabase>((gelöst, verworfen) => {
        const anfrage = indexedDB.open(name, SCHEMA_FASSUNG);
        anfrage.onupgradeneeded = (ereignis) => {
          const angelegt = anfrage.result;
          for (const table of TABLES) {
            if (!angelegt.objectStoreNames.contains(table)) {
              angelegt.createObjectStore(table, { keyPath: 'id' });
            }
          }
          /**
           * Aufstieg auf Fassung 2: die Übertragungsmarke wandert von `storeDate` auf
           * `transferred`.
           *
           * ⚠️ **Die Werte müssen mit, nicht nur das Schema.** IndexedDB kennt keine Spalten;
           * ein Satz **ohne** das Feld fällt durch `all('ExamLog', { transferred: 'N' })` und
           * bliebe für immer unsichtbar — die Bewertung ginge nie hinaus, ohne dass es
           * jemandem auffiele.
           */
          if ((ereignis.oldVersion || 0) < 2 && angelegt.objectStoreNames.contains('ExamLog')) {
            const speicher = anfrage.transaction!.objectStore('ExamLog');
            speicher.openCursor().onsuccess = (e) => {
              const zeiger = (e.target as IDBRequest<IDBCursorWithValue | null>).result;
              if (!zeiger) return;
              const satz = zeiger.value as Record<string, unknown>;
              if (satz.transferred === undefined) {
                // Ein Stempel in `storeDate` war die alte Bedeutung von „übertragen".
                satz.transferred = satz.storeDate ? 'Y' : 'N';
                // Und `storeDate` wird wieder, was es heisst: die Speicherzeit. Ohne Stempel
                // ist `creationDate` die beste verfügbare Schätzung.
                if (!satz.storeDate) satz.storeDate = satz.creationDate;
                zeiger.update(satz);
              }
              zeiger.continue();
            };
          }

          /**
           * Aufstieg auf Fassung 4: die Zahl der Ablehnungen kommt dazu.
           *
           * ⚠️ **Die Werte müssen mit, nicht nur das Schema** — dieselbe Falle wie oben. Ein
           * Satz ohne das Feld ergäbe bei `ablehnungen >= Grenze` einen Vergleich mit
           * `undefined`, und der ist immer falsch: die Datei würde ewig weiter geschickt,
           * genau das, was diese Fassung verhindern soll.
           */
          if (
            (ereignis.oldVersion || 0) < 4 &&
            angelegt.objectStoreNames.contains('Dateiuebertragung')
          ) {
            const speicher = anfrage.transaction!.objectStore('Dateiuebertragung');
            speicher.openCursor().onsuccess = (e) => {
              const zeiger = (e.target as IDBRequest<IDBCursorWithValue | null>).result;
              if (!zeiger) return;
              const satz = zeiger.value as Record<string, unknown>;
              if (satz.ablehnungen === undefined) {
                satz.ablehnungen = 0;
                zeiger.update(satz);
              }
              zeiger.continue();
            };
          }

          /*
           * Fassung 5: `Exam.simulative`.
           *
           * ⚠️ **Der Wert muss mit, nicht nur die Spalte.** IndexedDB kennt kein Schema — ein
           * Prüfungssatz ohne das Feld trägt `undefined`, und `undefined === 'Y'` ist falsch.
           * Das trifft hier zufällig die richtige Vorgabe („nicht simulativ"), aber eine
           * Abfrage über den Wert (`all('Exam', { simulative: 'N' })`) fände den Satz **nicht** —
           * er wäre für immer unsichtbar. Dieselbe Falle wie bei `ExamLog.transferred`.
           */
          /*
           * Fassung 7: `Exam.candidateInstructions` — dieselbe Falle, Vorgabe `null`.
           *
           * ⚠️ **Ein Durchgang für beide Fassungen.** Zwei Zeiger auf `Exam` im selben Aufstieg
           * läsen beide den alten Satz, und der zweite überschriebe, was der erste nachgetragen
           * hat (am 2026-10-09 im Test so passiert: `simulative` fehlte danach wieder).
           */
          const alt = ereignis.oldVersion || 0;
          if (alt < 8 && angelegt.objectStoreNames.contains('Exam')) {
            const speicher = anfrage.transaction!.objectStore('Exam');
            speicher.openCursor().onsuccess = (e) => {
              const zeiger = (e.target as IDBRequest<IDBCursorWithValue | null>).result;
              if (!zeiger) return;
              const satz = zeiger.value as Record<string, unknown>;
              let geaendert = false;
              if (alt < 5 && satz.simulative === undefined) {
                satz.simulative = 'N';
                geaendert = true;
              }
              if (satz.candidateInstructions === undefined) {
                satz.candidateInstructions = null;
                geaendert = true;
              }
              // Fassung 8: `devicePassword`, Vorgabe `null`.
              if (satz.devicePassword === undefined) {
                satz.devicePassword = null;
                geaendert = true;
              }
              if (geaendert) zeiger.update(satz);
              zeiger.continue();
            };
          }
        };
        anfrage.onsuccess = () => gelöst(anfrage.result);
        anfrage.onerror = () => verworfen(anfrage.error);
      });
    },

    async put(table, rows) {
      await merken(table);
      const tx = offen().transaction(table, 'readwrite');
      const speicher = tx.objectStore(table);
      for (const satz of rows) speicher.put(satz);
      await wartenTx(tx);
    },

    async all<T>(table: TableName, where?: Where) {
      const alle = await lesen<Record<string, unknown>>(table);
      return alle.filter((satz) => passt(satz, where)) as T[];
    },

    async remove(table, where) {
      await merken(table);
      const bleiben = (await lesen<Record<string, unknown>>(table)).filter(
        (satz) => !passt(satz, where),
      );
      const tx = offen().transaction(table, 'readwrite');
      const speicher = tx.objectStore(table);
      speicher.clear();
      for (const satz of bleiben) speicher.put(satz);
      await wartenTx(tx);
    },

    async tx<T>(fn: (t: Store) => Promise<T>): Promise<T> {
      // IndexedDB-Transaktionen enden, sobald der Aufrufstapel leer läuft — über `await`
      // hinweg tragen sie nicht. Deshalb wird der Stand vor der ersten Änderung gemerkt und
      // bei einem Fehler zurückgeschrieben.
      if (rückweg !== null) {
        throw new Error('tx() ist nicht verschachtelbar — eine Transaktion läuft bereits');
      }
      rückweg = [];
      try {
        const ergebnis = await fn(ablage);
        return ergebnis;
      } catch (fehler) {
        const zurück = rückweg ?? [];
        const restoreFehler: unknown[] = [];
        for (const { table, vorher } of zurück) {
          try {
            const tx = offen().transaction(table, 'readwrite');
            const speicher = tx.objectStore(table);
            speicher.clear();
            for (const satz of vorher) speicher.put(satz);
            await wartenTx(tx);
          } catch (e) {
            restoreFehler.push(e);
          }
        }
        // Begleitfehler anhängen, aber den ursprünglichen immer werfen.
        if (restoreFehler.length > 0 && typeof fehler === 'object' && fehler !== null) {
          (fehler as any).restoreErrors = restoreFehler;
        }
        throw fehler;
      } finally {
        rückweg = null;
      }
    },

    async close() {
      db?.close();
      db = null;
    },
  };
  return ablage;
}
